import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
} from "reactstrap";
import { getProducts, saveProduct, exportProductsToExcel, searchProducts } from "../../components/Api/GmsProduct";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getTaxes } from "../../components/Api/GmsTax";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { Eye, SquarePen, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  product_Id: null,
  product_Code: "",
  product_NameEn: "",
  product_NameAr: "",
  product_Description: "",
  product_CostPrice: 0, // Hidden field - kept as 0
  product_Amount: 0, // Hidden field - kept as 0
  product_NetAmount: 0, // Hidden field - kept as 0
  product_Business_Id: null,
  product_Supplier_Id: null,
  product_Status: true,
  product_CreatedDate: new Date().toISOString(),
  product_CreatedBy: 1,
  product_ModifiedDate: new Date().toISOString(),
  product_ModifiedBy: 1,
  product_Number: "",
  product_DiscountPercentage: 0, // Hidden field - kept as 0
  taxIds: [],
  Unit_Id: 1, // Default to 'Each' unit
  ExpiryRequired: false, // Default to No
};

const emptyFilter = {
  product_Id: null,
  product_Business_Id: null,
  product_Supplier_Id: null,
  product_Code: "",
  product_NameEn: "",
};

export default function ProductManagementPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsProductManagement');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsProductManagement');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsProductManagement');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsProductManagement');
  const isAdmin = isUserAdmin();

  const [products, setProducts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.product_Status : true;
    return { ...defaultForm, product_Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const navigate = useNavigate();
  const [taxes, setTaxes] = useState([]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  // Units states
  const [units, setUnits] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);
  const [allSuppliers, setAllSuppliers] = useState([]);
  // Supplier filter states
  const [supplierFilterOptions, setSupplierFilterOptions] = useState([]);
  const [supplierFilterLoading, setSupplierFilterLoading] = useState(false);
  const [filterSearchTimeout, setFilterSearchTimeout] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);

  // Product name search states for filter
  const [productNameSearchOptions, setProductNameSearchOptions] = useState([]);
  const [productNameSearchLoading, setProductNameSearchLoading] = useState(false);
  const [productNameSearchTimeout, setProductNameSearchTimeout] = useState(null);
  const [selectedProductNameOption, setSelectedProductNameOption] = useState(null);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
      },
      {
        header: "Product Code",
        accessorKey: "product_Code",
        enableColumnFilter: false,
      },
      {
        header: "Company Name",
        accessorKey: "business_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Product Name (En)",
        accessorKey: "product_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Product Name (Ar)",
        accessorKey: "product_NameAr",
        enableColumnFilter: false,
      },
      // Hidden columns - commented out
      // {
      //   header: "Cost Price",
      //   accessorKey: "product_CostPrice",
      //   enableColumnFilter: false,
      //   cell: ({ value }) => value ? value.toFixed(2) : '0.00',
      // },
      // {
      //   header: "Sales Price",
      //   accessorKey: "product_Amount",
      //   enableColumnFilter: false,
      //   cell: ({ value }) => value ? value.toFixed(2) : '0.00',
      // },
      // {
      //   header: "Net Amount",
      //   accessorKey: "product_NetAmount",
      //   enableColumnFilter: false,
      //   cell: ({ value }) => value ? value.toFixed(2) : '0.00',
      // },
      // {
      //   header: "Discount (%)",
      //   accessorKey: "product_DiscountPercentage",
      //   enableColumnFilter: false,
      //   cell: ({ value }) => value ? `${value.toFixed(2)}%` : '0.00%',
      // },
      {
        header: "Description",
        accessorKey: "product_Description",
        enableColumnFilter: false,
        cell: ({ value }) => value || "N/A",
      },
      {
        header: "Active",
        accessorKey: "product_Status",
        enableColumnFilter: false,
        cell: ({ value }) => (value ? "Yes" : "No"),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          hasEditOrHigher ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
              className="btn btn-link p-0"
              disabled={editLoadingRow === row.original.product_Id}
            >
              {editLoadingRow === row.original.product_Id ? (
                <ClipLoader size={20} color="black" />
              ) : (
                <SquarePen size={20} color="black" />
              )}
            </button>
          ) : null
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, page, pageSize]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (productNameSearchTimeout) {
        clearTimeout(productNameSearchTimeout);
      }
    };
  }, [productNameSearchTimeout]);

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadBusinesses();
      loadProducts(); // Load products on component mount
      loadTaxes(); // Load taxes on component mount
      loadUnits(); // Load units on component mount
      loadAllSuppliers(); // Load all suppliers for display
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadProducts();
    }
  }, [hasAppliedFilter]);

  // Update selected taxes when taxOptions are loaded and we're editing
  useEffect(() => {
    if (isEditing && taxOptions.length > 0 && form.taxIds && form.taxIds.length > 0) {
      const selectedTaxOptions = taxOptions.filter(tax =>
        form.taxIds.includes(tax.value)
      );
      setSelectedTaxes(selectedTaxOptions);
    } else if (isEditing && taxOptions.length > 0 && (!form.taxIds || form.taxIds.length === 0)) {
      // Clear selected taxes if product has no taxes
      setSelectedTaxes([]);
    }
  }, [taxOptions, isEditing, form.taxIds]);

  const loadProducts = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getProducts(filterData);
      // Normalize fields: map camelCase from API to PascalCase for form
      const normalizedData = data.map(product => ({
        ...product,
        ExpiryRequired: product.expiryRequired !== undefined ? product.expiryRequired : (product.ExpiryRequired !== undefined ? product.ExpiryRequired : false),
        Unit_Id: product.unit_Id !== undefined ? Number(product.unit_Id) : (product.Unit_Id !== undefined ? Number(product.Unit_Id) : null)
      }));
      setProducts(normalizedData);
      setTotalCount(normalizedData.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load products: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data);
      const options = data.map(business => ({
        value: business.business_Id,
        label: business.business_NameEn
      }));
      setBusinessOptions(options);
    } catch (error) {
      Swal.fire("Error", "Failed to load businesses: " + error.message, "error");
    }
  };

  const loadTaxes = async () => {
    try {
      // Only load active taxes
      const data = await getTaxes({ Tax_Active: true });
      setTaxes(data || []);
      const options = (data || []).map(tax => ({
        value: tax.tax_Id,
        label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
      }));
      setTaxOptions(options);
    } catch (error) {
      console.error("Error loading taxes:", error);
      // Don't show error alert for taxes as it's optional
      setTaxOptions([]);
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
      // The getActiveUnits function already returns the correct format
      setUnitOptions(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      // Don't show error alert for units as it's optional
      setUnitOptions([]);
    }
  };

  const loadAllSuppliers = async () => {
    try {
      // Load all suppliers for display purposes
      const response = await searchSuppliers("", 1, 1000); // Get all suppliers
      setAllSuppliers(response.items || []);
    } catch (error) {
      console.error("Error loading suppliers:", error);
      setAllSuppliers([]);
    }
  };

  // Manual search function for supplier filter
  const searchSuppliersForFilter = async (inputValue) => {
    console.log('🔍 searchSuppliersForFilter called with inputValue:', inputValue);

    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';

    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierFilterOptions([]);
      return;
    }

    try {
      setSupplierFilterLoading(true);
      console.log('🚀 Making API call to searchSuppliers for filter with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);

      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierFilterOptions([]);
        return;
      }

      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';

        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) ||
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );

        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }

        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || 'N/A'}`;

        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });

      console.log('📋 Formatted filter options:', formattedOptions);
      setSupplierFilterOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers for filter:', error);
      setSupplierFilterOptions([]);
    } finally {
      setSupplierFilterLoading(false);
    }
  };

  // Manual search function for product name filter
  const searchProductNameManually = async (inputValue) => {
    console.log('🔍 searchProductNameManually called with inputValue:', inputValue);

    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';

    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setProductNameSearchOptions([]);
      return;
    }

    try {
      setProductNameSearchLoading(true);
      console.log('🚀 Making API call to searchProducts with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchProducts(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);

      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setProductNameSearchOptions([]);
        return;
      }

      const formattedOptions = response.items.map(product => ({
        value: product.product_NameEn,
        label: `${product.product_NameEn || 'N/A'} — ${product.product_Code || 'N/A'}`,
        product: product
      }));

      console.log('📋 Formatted options:', formattedOptions);
      setProductNameSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching products:', error);
      setProductNameSearchOptions([]);
    } finally {
      setProductNameSearchLoading(false);
    }
  };

  // Manual search function for supplier search
  const searchSuppliersManually = async (inputValue) => {
    console.log('🔍 searchSuppliersManually called with inputValue:', inputValue);

    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';

    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }

    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);

      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }

      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';

        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) ||
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );

        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }

        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || 'N/A'}`;

        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });

      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Commented out calculation function as fields are hidden
  // const calculateNetAmount = (amount, discountPercentage, taxIds) => {
  //   if (!amount || amount <= 0) return 0;
  //   
  //   let netAmount = amount;
  //   
  //   // Apply discount
  //   if (discountPercentage && discountPercentage > 0) {
  //     const discountAmount = (amount * discountPercentage) / 100;
  //     netAmount = amount - discountAmount;
  //   }
  //   
  //   // Apply taxes
  //   if (taxIds && taxIds.length > 0) {
  //     const totalTaxPercentage = taxIds.reduce((total, taxId) => {
  //       const tax = taxes.find(t => t.tax_Id === taxId);
  //       return total + (tax ? tax.tax_Perc : 0);
  //     }, 0);
  //     
  //     const taxAmount = (netAmount * totalTaxPercentage) / 100;
  //     netAmount = netAmount + taxAmount;
  //   }
  //   
  //   return Math.round(netAmount * 100) / 100; // Round to 2 decimal places
  // };

  const handleFormChange = (field, value) => {
    // Special handling for Product Code validation
    if (field === "product_Code") {
      // Remove spaces and limit to 10 characters
      value = value.replace(/\s/g, '').substring(0, 10);
    }

    setForm(prev => {
      const updatedForm = {
        ...prev,
        [field]: value
      };

      // Commented out calculation logic as fields are hidden
      // // Calculate net amount when amount, discount, or taxes change
      // if (field === "product_Amount" || field === "product_DiscountPercentage" || field === "taxIds") {
      //   updatedForm.product_NetAmount = calculateNetAmount(
      //     field === "product_Amount" ? value : updatedForm.product_Amount,
      //     field === "product_DiscountPercentage" ? value : updatedForm.product_DiscountPercentage,
      //     field === "taxIds" ? value : updatedForm.taxIds
      //   );
      // }

      return updatedForm;
    });
  };

  const handleTaxChange = (selected) => {
    setSelectedTaxes(selected || []);
    const taxIds = selected ? selected.map(tax => tax.value) : [];
    setForm(prev => {
      const updatedForm = {
        ...prev,
        taxIds: taxIds
      };

      // Commented out calculation logic as fields are hidden
      // // Recalculate net amount when taxes change
      // updatedForm.product_NetAmount = calculateNetAmount(
      //   updatedForm.product_Amount,
      //   updatedForm.product_DiscountPercentage,
      //   taxIds
      // );

      return updatedForm;
    });
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only allow updates, not inserts
    if (!isEditing) {
      Swal.fire("Error", "Please use the Add Product page to create new products", "error");
      return;
    }

    // Validation
    if (!form.product_Business_Id) {
      Swal.fire("Error", "Please select a business", "error");
      return;
    }

    if (!form.product_Supplier_Id) {
      Swal.fire("Error", "Please select a supplier", "error");
      return;
    }

    if (!form.product_NameEn.trim()) {
      Swal.fire("Error", "Please enter product name in English", "error");
      return;
    }

    // Validate Product Code length
    if (form.product_Code && form.product_Code.length > 10) {
      Swal.fire("Error", "Product Code cannot exceed 10 characters", "error");
      return;
    }

    // Validate Unit selection
    if (!form.Unit_Id) {
      Swal.fire("Error", "Please select a unit", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();

      // Prepare update data
      const productData = {
        ...form,
        product_CreatedBy: form.product_CreatedBy,
        product_ModifiedBy: session.userId,
        product_CreatedDate: form.product_CreatedDate,
        product_ModifiedDate: new Date().toISOString(),
        taxIds: selectedTaxes.map(tax => tax.value),
        ExpiryRequired: form.ExpiryRequired !== undefined ? form.ExpiryRequired : false,
      };

      const result = await saveProduct(productData);

      if (result) {
        Swal.fire("Success", "Product updated successfully!", "success");
        handleClear();
        loadProducts();
      }
    } catch (error) {
      let errorMessage = "Failed to update product";

      // Extract error message from API response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (product) => {
    setEditLoadingRow(product.product_Id);
    try {
      const updatedProduct = {
        ...product,
        product_CreatedDate: product.product_CreatedDate || new Date().toISOString(),
        product_ModifiedDate: new Date().toISOString(),
        taxIds: product.taxIds || [],
        // Map expiryRequired (camelCase from API) to ExpiryRequired (PascalCase for form)
        ExpiryRequired: product.expiryRequired !== undefined ? product.expiryRequired : (product.ExpiryRequired !== undefined ? product.ExpiryRequired : false),
        // Map unit_Id (camelCase from API) to Unit_Id (PascalCase for form) and ensure it's a number
        Unit_Id: product.unit_Id !== undefined ? Number(product.unit_Id) : (product.Unit_Id !== undefined ? Number(product.Unit_Id) : null),
      };

      // Commented out calculation logic as fields are hidden
      // // Calculate net amount for the product being edited
      // updatedProduct.product_NetAmount = calculateNetAmount(
      //   updatedProduct.product_Amount || 0,
      //   updatedProduct.product_DiscountPercentage || 0,
      //   updatedProduct.taxIds || []
      // );

      setForm(updatedProduct);

      // Set selected taxes for the dropdown
      if (product.taxIds && product.taxIds.length > 0) {
        const selectedTaxOptions = taxOptions.filter(tax =>
          product.taxIds.includes(tax.value)
        );
        setSelectedTaxes(selectedTaxOptions);
      } else {
        setSelectedTaxes([]);
      }

      // Set selected supplier for the dropdown
      if (product.product_Supplier_Id) {
        // Try to find supplier in allSuppliers or search for it
        const foundSupplier = allSuppliers.find(s => s.supplier_Id === product.product_Supplier_Id);
        if (foundSupplier) {
          setSelectedSupplierOption({
            value: product.product_Supplier_Id,
            label: `${foundSupplier.supplier_NameEn || 'N/A'} — ${foundSupplier.supplier_Code || foundSupplier.supplier_ContactNo || 'N/A'}`
          });
        } else {
          // If not found, search for it
          try {
            const response = await searchSuppliers("", 1, 1000);
            const found = response.items.find(s => s.supplier_Id === product.product_Supplier_Id);
            if (found) {
              setSelectedSupplierOption({
                value: product.product_Supplier_Id,
                label: `${found.supplier_NameEn || 'N/A'} — ${found.supplier_Code || found.supplier_ContactNo || 'N/A'}`
              });
            } else {
              setSelectedSupplierOption({
                value: product.product_Supplier_Id,
                label: `Supplier ID: ${product.product_Supplier_Id}`
              });
            }
          } catch (error) {
            console.error('Error fetching supplier details:', error);
            setSelectedSupplierOption({
              value: product.product_Supplier_Id,
              label: `Supplier ID: ${product.product_Supplier_Id}`
            });
          }
        }
      } else {
        setSelectedSupplierOption(null);
      }

      setIsEditing(true);
      setShowEditForm(true); // Show edit form
    } catch (error) {
      Swal.fire("Error", "Failed to load product data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.product_Status : true;
    setForm({ ...defaultForm, product_Status: defaultStatus });
    setSelectedTaxes([]);
    setSelectedSupplierOption(null);
    setIsEditing(false);
    setShowEditForm(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setSelectedSupplierFilterOption(null);
    setSelectedProductNameOption(null);
    setProductNameSearchOptions([]);
    setHasAppliedFilter(false);
    setProducts([]);
    setTotalCount(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.product_Id !== null && filter.product_Id !== undefined && { product_Id: filter.product_Id }),
        ...(filter.product_Code && filter.product_Code.trim() !== '' && { product_Code: filter.product_Code }),
        ...(filter.product_Business_Id !== null && filter.product_Business_Id !== undefined && { product_Business_Id: filter.product_Business_Id }),
        ...(filter.product_Supplier_Id !== null && filter.product_Supplier_Id !== undefined && { product_Supplier_Id: filter.product_Supplier_Id }),
        ...(filter.product_NameEn && filter.product_NameEn.trim() !== '' && filter.product_NameEn.trim().length >= 3 && { product_NameEn: filter.product_NameEn })
      };

      await exportProductsToExcel(cleanFilter);
      Swal.fire("Success", "Product report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting products:", error);
      Swal.fire("Error", "Failed to export product report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedProducts = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return products.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };


  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_Id === businessId);
    return business ? business.business_NameEn : "Unknown";
  };

  const getUnitName = (unitId) => {
    if (!unitId) return "N/A";
    // Convert to number to ensure type matching
    const numericUnitId = Number(unitId);
    const unit = units.find(u => u.value === numericUnitId);
    if (unit) {
      // Extract just the name part after the dash (e.g., "001 - Each" -> "Each")
      const namePart = unit.label.split(' - ')[1];
      return namePart || unit.label;
    }
    return `Unit ID: ${unitId}`;
  };


  const getTaxNames = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return "N/A";
    const taxNames = taxIds.map(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      return tax ? tax.tax_NameEn : `Tax ID: ${taxId}`;
    });
    return taxNames.join(", ");
  };

  const getSupplierName = (supplierId) => {
    if (!supplierId) return "N/A";
    // Find supplier from all suppliers
    const supplier = allSuppliers.find(s => s.supplier_Id === supplierId);
    return supplier ? supplier.supplier_NameEn : `ID: ${supplierId}`;
  };

  const selectedBusiness = businessOptions.find(b => b.value === form.product_Business_Id);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Product Management" breadcrumbItem="Spa" />

        {/* Inline Edit Form - Hidden by default, shown when editing */}
        {showEditForm && hasEditOrHigher && (
          <Row className="mb-4">
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">Edit Product</h4>
                    <Button color="secondary" size="sm" onClick={handleClear}>
                      <i className="bx bx-x me-1"></i>
                      Close
                    </Button>
                  </div>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_Business_Id">Company <span className="text-danger">*</span></Label>
                          <Select
                            id="product_Business_Id"
                            value={selectedBusiness || null}
                            onChange={(selected) => handleFormChange("product_Business_Id", selected?.value || 0)}
                            options={businessOptions}
                            placeholder="Select Company"
                            isClearable
                            isSearchable
                            maxMenuHeight={80}
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                minHeight: '38px',
                                border: '1px solid #ced4da',
                                borderRadius: '0.375rem',
                                '&:hover': {
                                  borderColor: '#adb5bd'
                                },
                                '&:focus': {
                                  borderColor: '#80bdff',
                                  boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                                }
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                                color: state.isSelected ? 'white' : '#495057',
                                padding: '8px 12px',
                                cursor: 'pointer'
                              })
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_Supplier_Id">Supplier <span className="text-danger">*</span></Label>
                          <Select
                            value={selectedSupplierOption}
                            onChange={(option) => {
                              setSelectedSupplierOption(option);
                              handleFormChange('product_Supplier_Id', option?.value || null);
                            }}
                            options={supplierSearchOptions}
                            placeholder="Search by name..."
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                minHeight: '38px',
                                height: '38px',
                                border: '1px solid #ced4da',
                                borderRadius: '0.375rem',
                                '&:hover': {
                                  borderColor: '#adb5bd'
                                },
                                '&:focus': {
                                  borderColor: '#80bdff',
                                  boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                                }
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                                color: state.isSelected ? 'white' : '#495057',
                                padding: '8px 12px',
                                cursor: 'pointer'
                              })
                            }}
                            isClearable
                            isSearchable
                            isDisabled={isEditing}
                            isLoading={supplierSearchLoading}
                            noOptionsMessage={() => "Type at least 3 characters"}
                            loadingMessage={() => "Searching..."}
                            filterOption={null}
                            onInputChange={(inputValue) => {
                              console.log('🔤 Supplier input changed:', inputValue);

                              // Clear previous timeout
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }

                              // Trim the input value before searching
                              const trimmedValue = inputValue ? inputValue.trim() : '';

                              // Set new timeout
                              const timeout = setTimeout(() => {
                                if (trimmedValue.length >= 3) {
                                  searchSuppliersManually(trimmedValue);
                                } else {
                                  setSupplierSearchOptions([]);
                                }
                              }, 300);
                              setSearchTimeout(timeout);

                              return inputValue;
                            }}
                            onMenuOpen={() => console.log('📂 Supplier menu opened')}
                            onMenuClose={() => console.log('📁 Supplier menu closed')}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_Code">Product Code (Optional)</Label>
                          <Input
                            type="text"
                            id="product_Code"
                            value={form.product_Code}
                            onChange={(e) => handleFormChange("product_Code", e.target.value)}
                            placeholder="Product Code"
                            maxLength="10"
                          />
                          <small className="text-muted mt-2 d-block">
                            Maximum 10 characters, no spaces allowed
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_NameEn">Product Name (English) <span className="text-danger">*</span></Label>
                          <Input
                            type="text"
                            id="product_NameEn"
                            value={form.product_NameEn}
                            onChange={(e) => handleFormChange("product_NameEn", e.target.value)}
                            placeholder="Enter product name in English"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_NameAr">Product Name (Arabic)</Label>
                          <Input
                            type="text"
                            id="product_NameAr"
                            value={form.product_NameAr}
                            onChange={(e) => handleFormChange("product_NameAr", e.target.value)}
                            placeholder="Enter product name in Arabic"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="product_TaxIds">Taxes (Optional)</Label>
                          <Select
                            id="product_TaxIds"
                            value={selectedTaxes}
                            onChange={handleTaxChange}
                            options={taxOptions}
                            isMulti
                            placeholder="Select Taxes"
                            isClearable
                            maxMenuHeight={200}
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                minHeight: '38px',
                                border: '1px solid #ced4da',
                                borderRadius: '0.375rem',
                                '&:hover': {
                                  borderColor: '#adb5bd'
                                },
                                '&:focus': {
                                  borderColor: '#80bdff',
                                  boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                                }
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                                color: state.isSelected ? 'white' : '#495057',
                                padding: '8px 12px',
                                cursor: 'pointer'
                              })
                            }}
                          />
                          <small className="text-muted mt-2 d-block">
                            Select one or more taxes to apply to this product
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="product_Description">Description (Optional)</Label>
                          <Input
                            type="textarea"
                            id="product_Description"
                            rows="3"
                            value={form.product_Description}
                            onChange={(e) => handleFormChange("product_Description", e.target.value)}
                            placeholder="Enter product description"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="product_Unit_Id">Unit <span className="text-danger">*</span></Label>
                          <Select
                            id="product_Unit_Id"
                            value={unitOptions.find(option => Number(option.value) === Number(form.Unit_Id)) || null}
                            onChange={(selectedOption) => handleFormChange("Unit_Id", selectedOption ? Number(selectedOption.value) : null)}
                            options={unitOptions}
                            placeholder="Select unit"
                            isClearable={false}
                            isSearchable={true}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label>Expiry Date Required</Label>
                          <div className="d-flex gap-3 mt-2">
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="expiry_yes"
                                name="ExpiryRequired"
                                checked={form.ExpiryRequired === true}
                                onChange={() => handleFormChange("ExpiryRequired", true)}
                                className="form-check-input"
                              />
                              <Label for="expiry_yes" className="form-check-label">
                                Yes
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id="expiry_no"
                                name="ExpiryRequired"
                                checked={form.ExpiryRequired === false}
                                onChange={() => handleFormChange("ExpiryRequired", false)}
                                className="form-check-input"
                              />
                              <Label for="expiry_no" className="form-check-label">
                                No
                              </Label>
                            </div>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    {hasDeletePermission && (
                      <Row>
                        <Col md={12}>
                          <FormGroup className="mb-4">
                            <div className="form-check form-switch">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="product_Status"
                                checked={form.product_Status}
                                onChange={(e) => handleFormChange("product_Status", e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor="product_Status">
                                {form.product_Status ? "Active" : "Inactive"}
                              </label>
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col md={12}>
                        <div className="d-flex justify-content-end">
                          <Button color="primary" type="submit" disabled={saveLoading} className="me-2">
                            {saveLoading ? <ClipLoader size={20} color="white" /> : "Update"}
                          </Button>
                          <Button color="secondary" type="button" onClick={handleClear}>
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={12}>
            {/* Filter Section - Hidden by default */}
            {showFilters && (
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title">Filter Products</h5>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filter_product_Business_Id">Company</Label>
                        <Select
                          id="filter_product_Business_Id"
                          value={businessOptions.find(b => b.value === filter.product_Business_Id) || null}
                          onChange={(selected) => handleFilterChange("product_Business_Id", selected?.value || null)}
                          options={businessOptions}
                          placeholder="Select Company"
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filter_product_Supplier_Id">Supplier</Label>
                        <Select
                          value={selectedSupplierFilterOption}
                          onChange={(option) => {
                            setSelectedSupplierFilterOption(option);
                            handleFilterChange("product_Supplier_Id", option?.value || null);
                          }}
                          options={supplierFilterOptions}
                          placeholder="Search by name..."
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              height: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                          isClearable
                          isSearchable
                          isLoading={supplierFilterLoading}
                          noOptionsMessage={() => "Type at least 3 characters"}
                          loadingMessage={() => "Searching..."}
                          filterOption={null}
                          onInputChange={(inputValue) => {
                            console.log('🔤 Supplier filter input changed:', inputValue);

                            // Clear previous timeout
                            if (filterSearchTimeout) {
                              clearTimeout(filterSearchTimeout);
                            }

                            // Trim the input value before searching
                            const trimmedValue = inputValue ? inputValue.trim() : '';

                            // Set new timeout
                            const timeout = setTimeout(() => {
                              if (trimmedValue.length >= 3) {
                                searchSuppliersForFilter(trimmedValue);
                              } else {
                                setSupplierFilterOptions([]);
                              }
                            }, 300);
                            setFilterSearchTimeout(timeout);

                            return inputValue;
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filter_product_NameEn">Product Name</Label>
                        <Select
                          id="filter_product_NameEn"
                          value={selectedProductNameOption}
                          onChange={(option) => {
                            setSelectedProductNameOption(option);
                            handleFilterChange("product_NameEn", option?.value || "");
                          }}
                          options={productNameSearchOptions}
                          placeholder="Search by name..."
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              height: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                              '&:hover': {
                                borderColor: '#adb5bd'
                              },
                              '&:focus': {
                                borderColor: '#80bdff',
                                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#495057',
                              padding: '8px 12px',
                              cursor: 'pointer'
                            })
                          }}
                          isClearable
                          isSearchable
                          isLoading={productNameSearchLoading}
                          noOptionsMessage={() => "Type at least 3 characters"}
                          loadingMessage={() => "Searching..."}
                          filterOption={null}
                          onInputChange={(inputValue) => {
                            console.log('🔤 Product name filter input changed:', inputValue);

                            // Clear previous timeout
                            if (productNameSearchTimeout) {
                              clearTimeout(productNameSearchTimeout);
                            }

                            // Trim the input value before searching
                            const trimmedValue = inputValue ? inputValue.trim() : '';

                            // Set new timeout
                            const timeout = setTimeout(() => {
                              if (trimmedValue.length >= 3) {
                                searchProductNameManually(trimmedValue);
                              } else {
                                setProductNameSearchOptions([]);
                              }
                            }, 300);
                            setProductNameSearchTimeout(timeout);

                            return inputValue;
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filter_product_Code">Product Code</Label>
                        <Input
                          type="text"
                          id="filter_product_Code"
                          value={filter.product_Code}
                          onChange={(e) => handleFilterChange("product_Code", e.target.value)}
                          placeholder="Enter product code"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <div className="d-flex justify-content-end">
                        <Button color="secondary" type="button" onClick={handleClearFilter} className="me-2">
                          Clear Filter
                        </Button>
                        <Button color="primary" type="button" onClick={handleApplyFilter}>
                          Apply Filter
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Products Table */}
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0">
                      Product List ({totalCount} records)
                    </h5>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="waves-effect waves-light"
                    >
                      <i className={`bx ${showFilters ? 'bx-filter-alt' : 'bx-filter'} me-1`}></i>
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {hasEditOrHigher && (
                      <Button
                        color="primary"
                        onClick={() => navigate('/GmsAddProduct')}
                        className="waves-effect waves-light"
                      >
                        <i className="bx bx-plus-circle me-1"></i>
                        Add New Product
                      </Button>
                    )}
                    <Button
                      color="success"
                      onClick={handleExportExcel}
                      disabled={loading || totalCount === 0}
                      className="waves-effect waves-light"
                    >
                      <Download className="me-1" size={16} />
                      Export Excel
                    </Button>
                    <Label className="me-2 mb-0">Show:</Label>
                    <Input
                      type="select"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value));
                        setPage(1);
                      }}
                      style={{ width: "auto" }}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Input>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center">
                    <ClipLoader size={50} color="black" />
                  </div>
                ) : (
                  <>
                    <div style={{
                      overflowX: 'scroll',
                      maxWidth: '100%',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      WebkitOverflowScrolling: 'touch'
                    }}>
                      <Table bordered style={{
                        minWidth: '1400px',
                        width: 'max-content',
                        marginBottom: 0
                      }}>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Actions</th>
                            <th>Product Code</th>
                            <th>Company Name</th>
                            <th>Supplier</th>
                            <th>Product Name (En)</th>
                            <th>Product Name (Ar)</th>
                            {/* Hidden columns */}
                            {/* <th>Cost Price</th>
                                      <th>Sales Price</th>
                                      <th>Net Amount</th>
                                      <th>Discount (%)</th> */}
                            <th>Unit</th>
                            <th>Expiry Required</th>
                            <th>Description</th>
                            <th>Taxes</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.length === 0 ? (
                            <tr>
                              <td colSpan="12" className="text-center">
                                No products found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedProducts().map((product, index) => (
                              <tr key={product.product_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleEdit(product)}
                                      disabled={editLoadingRow === product.product_Id}
                                    >
                                      {editLoadingRow === product.product_Id ? (
                                        <ClipLoader size={16} color="white" />
                                      ) : (
                                        "Edit"
                                      )}
                                    </Button>
                                  </div>
                                </td>
                                <td>{product.product_Code}</td>
                                <td>{getBusinessName(product.product_Business_Id)}</td>
                                <td>{getSupplierName(product.product_Supplier_Id)}</td>
                                <td>{product.product_NameEn}</td>
                                <td>{product.product_NameAr}</td>
                                {/* Hidden data cells */}
                                {/* <td className="text-end">{product.product_CostPrice ? product.product_CostPrice.toFixed(2) : '0.00'}</td>
                                          <td className="text-end">{product.product_Amount ? product.product_Amount.toFixed(2) : '0.00'}</td>
                                          <td className="text-end">{product.product_NetAmount ? product.product_NetAmount.toFixed(2) : '0.00'}</td>
                                          <td className="text-end">{product.product_DiscountPercentage ? `${product.product_DiscountPercentage.toFixed(2)}%` : '0.00%'}</td> */}
                                <td>{getUnitName(product.Unit_Id || product.unit_Id)}</td>
                                <td>
                                  <span className={`badge ${(product.expiryRequired || product.ExpiryRequired) ? 'bg-warning' : 'bg-secondary'}`}>
                                    {(product.expiryRequired || product.ExpiryRequired) ? "Yes" : "No"}
                                  </span>
                                </td>
                                <td>{product.product_Description || "N/A"}</td>
                                <td>{getTaxNames(product.taxIds)}</td>
                                <td>
                                  <span className={`badge ${product.product_Status ? 'bg-success' : 'bg-danger'}`}>
                                    {product.product_Status ? "Active" : "Inactive"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                      style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                      <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                        Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>

                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;

                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 